import AuthCli from './AuthCli'

/**
 * JavaScript version of the Outbox service API.
 */
export default class InboxCli {

  //
  // Path constants:
  //
  static get SERVICE_ROUTE() { return 'inbox' }
  static get FEEDBACK_PATH() { return 'feedback' }
  static get PAGE_SIZE_QUERY() { return 'pagesize' }
  static get PAGE_QUERY() { return 'page' }
  static get INCLUDE_READ_QUERY() { return 'includeread' }
  static get INCLUDE_STATES_QUERY() { return 'includestates' }
  static get INCLUDE_PROOFS_QUERY() { return 'includeproofs' }
  static get INCLUDE_CERTS_QUERY() { return 'includecerts' }
  static get INCLUDE_DOCUMENTS_QUERY() { return 'includedocuments' }
  static get INCLUDE_STAMPED_COPIES_QUERY() { return 'includestampedcopies' }
  static get INCLUDE_REPORTS_QUERY() { return 'includereports' }
  static get BILLABLE_FEEDBACK_PATH() { return 'billablefeedback' }
  static get STATES_PATH() { return 'states' }
  static get RETURN_IDS_QUERY() { return 'returnids' }
  static get PROOFS_PATH() { return 'proofs' }
  static get CERTS_PATH() { return 'certs' }
  static get DOCUMENTS_PATH() { return 'documents' }
  static get STAMPED_COPY_PATH() { return 'stampedcopy' }
  static get REPORTS_PATH() { return 'reports' }
  static get SHIPMENTS_PATH() { return 'shipments' }
  static get SHIPMENT_CODE_PATH() { return 'shipmentcode' }
  static get SHIPMENT_CODES_QUERY() { return 'shipmentcodes' }
  static get COUNT_PATH() { return 'count' }
  static get FLAG_READ_PATH() { return 'read' }
  static get FLAG_UNREAD_PATH() { return 'unread' }
  static get WATCHERS_PATH() { return 'WATCH_WAKEUP_PATH' }
  static get WATCH_WAKEUP_PATH() { return 'watchwakeup' }
  static get BILLABLE_WATCHERS_PATH() { return 'billablewatchers' }
  static get FEEDBACK_FILE_PATH() { return 'feedbackfile' }
  static get RETURN_ID_PATH() { return 'returnid' }
  static get FLAG_AS_READ_QUERY() { return 'flagasread' }
  static get BILLABLE_FEEDBACK_FILE_PATH() { return 'billablefeedbackfile' }

  // Other constants:
  static get RETURN_STAGE_STATE() { return 'STATE' }
  static get RETURN_STAGE_CERT() { return 'CERT' }
  static get RETURN_STAGE_PROOF() { return 'PROOF' }
  static get RETURN_STAGE_DOCUMENT() { return 'DOCUMENT' }
  static get RETURN_STAGE_STAMPED_COPY() { return 'STAMPED_COPY' }
  static get RETURN_STAGE_RETURN() { return 'RETURN' }

  // URL prefix for debugging environment vs behind reverse proxy environment.
  static get devPrefix() { return (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') ? 'http://localhost:6014/' : window.location.protocol + '//' + window.location.host + '/api/' }

  /**
    * Provides the list of feedback returns available for the calling user.
    * This is a paging request, the response may not contain all feedback.
    * For paging considerations, the collection of feedback returns of the calling user is provided sorted by incoming time descending.
   * @param {int} pageSize Paging argument: maximum amount of drafts to be provided in this call
   * @param {int} page Paging argument: the page to provide, 0-based
   * @param {boolean} includeRead Whether desired to receive return units flagged as read, otherwise only unread feedback will be included
   * @param {boolean} includeStates Whether desired to receive state changes
   * @param {boolean} includeProofs Whether desired to receive proofs of delivery
   * @param {boolean} includeCerts Whether desired to receive certificates
   * @param {boolean} includeReports Whether desired to receive reports availability
   * @param {boolean} includeStampedCopies Whether desired to receive stamped copies
   * @returns {FeedbackReturn[]} (string returnId, string returnType, string description, string shipmentCode, DateTime recordedTime, bool read)
  */
  static async GetFeedback(pageSize, page, includeRead, includeStates, includeProofs, includeCerts, includeDocuments, includeReports, includeStampedCopies = false) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH + "?" +
      this.PAGE_SIZE_QUERY + "=" + pageSize + "&" +
      this.PAGE_QUERY + "=" + page + "&" +
      this.INCLUDE_READ_QUERY + "=" + includeRead + "&" +
      this.INCLUDE_STATES_QUERY + "=" + includeStates + "&" +
      this.INCLUDE_PROOFS_QUERY + "=" + includeProofs + "&" +
      this.INCLUDE_CERTS_QUERY + "=" + includeCerts + "&" +
      this.INCLUDE_DOCUMENTS_QUERY + "=" + includeDocuments + "&" +
      this.INCLUDE_REPORTS_QUERY + "=" + includeReports + "&" +
      this.INCLUDE_STAMPED_COPIES_QUERY + "=" + includeStampedCopies
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the list of feedback returns available for any user in the current billable.
   * This is to be used by working group administrators which are allowed to access content from any user in the billable.
   * This is a paging request, the response may not contain all feedback.
   * For paging considerations, the collection of feedback returns of the calling user is provided sorted by incoming time descending.
   * @param {int} pageSize Paging argument: maximum amount of drafts to be provided in this call
   * @param {int} page Paging argument: the page to provide, 0-based
   * @param {boolean} includeRead Whether desired to receive return units flagged as read, otherwise only unread feedback will be included
   * @param {boolean} includeStates Whether desired to receive state changes
   * @param {boolean} includeProofs Whether desired to receive proofs of delivery
   * @param {boolean} includeCerts Whether desired to receive certificates
   * @param {boolean} includeReports Whether desired to receive reports availability
   * @param {boolean} includeStampedCopies Whether desired to receive stamped copies
   * @returns {FeedbackReturn[]} (string returnId, string returnType, string description, string shipmentCode, DateTime recordedTime, bool read)
  */
  static async GetFeedbackOfBillable(pageSize, page, includeRead, includeStates, includeProofs, includeCerts, includeDocuments, includeReports, includeStampedCopies = false) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_FEEDBACK_PATH + "?" +
      this.PAGE_SIZE_QUERY + "=" + pageSize + "&" +
      this.PAGE_QUERY + "=" + page + "&" +
      this.INCLUDE_READ_QUERY + "=" + includeRead + "&" +
      this.INCLUDE_STATES_QUERY + "=" + includeStates + "&" +
      this.INCLUDE_PROOFS_QUERY + "=" + includeProofs + "&" +
      this.INCLUDE_CERTS_QUERY + "=" + includeCerts + "&" +
      this.INCLUDE_DOCUMENTS_QUERY + "=" + includeDocuments + "&" +
      this.INCLUDE_REPORTS_QUERY + "=" + includeReports + "&" +
      this.INCLUDE_STAMPED_COPIES_QUERY + "=" + includeStampedCopies
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides detailed information about one or more state changes occurred to shipments.
   * @returns {StateInfo[]} (string returnId, string shipmentCode, string stateCode, string stateData, string stage, DateTime? timestamp)
  */
  static async GetStates(returnIds) {
    const stateIds = Array.isArray(returnIds) ? returnIds.filter(returnId => returnId) : []
    const maxUrlLength = 1800
    const pathPrefix = this.devPrefix + this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH + '/' + this.STATES_PATH + "?" + this.RETURN_IDS_QUERY + "="
    const states = []

    // Bound the encoded URL, not the ID count: JSON escaping also counts towards proxy/query limits.
    for (let index = 0; index < stateIds.length;) {
      const batch = []
      let path = ""
      while (index < stateIds.length) {
        const nextPath = pathPrefix + encodeURIComponent(JSON.stringify([...batch, stateIds[index]]))
        if (nextPath.length > maxUrlLength) break
        batch.push(stateIds[index++])
        path = nextPath
      }
      if (batch.length === 0) return "[ERR] Feedback state identifier exceeds the request URL limit"

      try {
        const response = await AuthCli.Fetch(path, 'GET', null)
        if (typeof response === "string" && response.startsWith("[ERR")) return response
        if (!Array.isArray(response)) return "[ERR] Unable to retrieve feedback states: invalid response"
        states.push(...response)
      } catch (error) {
        return "[ERR] Unable to retrieve feedback states"
      }
    }

    return states
  }

  /**
   * Provides detailed information about one or more proofs of delivery available associated with shipments.
   * @returns {ProofInfo[]} (string returnId, string shipmentCode, string stateCode, string stateData, string stage, DateTime? timestamp)
  */
  static async GetProofs(returnIds) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH + '/' + this.PROOFS_PATH + "?" + this.RETURN_IDS_QUERY + "=" + JSON.stringify(returnIds)
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides detailed information about one or more certificates available associated with shipments.
   * @returns {CertInfo[]} (string returnId, string shipmentCode, string certType, string documentFileName, int documentPages, int documentSize, DateTime? timestamp)
  */
  static async GetCerts(returnIds) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH + '/' + this.CERTS_PATH + "?" + this.RETURN_IDS_QUERY + "=" + JSON.stringify(returnIds)
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides detailed information about one or more documents available associated with shipments.
   * @returns {DocumentInfo[]} (string returnId, string shipmentCode, string documentFileName, int documentPages, int documentSize, DateTime? timestamp)
   */
  static async GetDocuments(returnIds) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH + '/' + this.DOCUMENTS_PATH + "?" + this.RETURN_IDS_QUERY + "=" + JSON.stringify(returnIds)
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides detailed information about one or more stamped copies available associated with shipments.
   * @returns {StampedCopyInfo[]} (string returnId, string shipmentCode, string stampedCopyType, string documentFileName, int documentPages, long documentSize, DateTime? timestamp)
   */
  static async GetStampedCopy(returnIds) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH + '/' + this.STAMPED_COPY_PATH + "?" + this.RETURN_IDS_QUERY + "=" + JSON.stringify(returnIds)
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides detailed information about one or more reports available associated with shipments.
   * @returns {ReportInfo[]} (TODO)
  */
  static async GetReports(returnIds) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH + '/' + this.REPORTS_PATH + "?" + this.RETURN_IDS_QUERY + "=" + JSON.stringify(returnIds)
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the list of feedback returns available associated with a particular shipment.
   * @param {int} shipmentCode External identifier of the shipment asking for its feedback
   * @param {boolean} includeStates Whether desired to receive state changes
   * @param {boolean} includeProofs Whether desired to receive proofs of delivery
   * @param {boolean} includeCerts Whether desired to receive certificates
   * @param {boolean} includeStampedCopies Whether desired to receive stamped copies
   * @returns {FeedbackReturn[]} (string returnId, string returnType, string description, string shipmentCode, DateTime recordedTime, bool read)
  */
  static async GetFeedbackOfShipment(shipmentCode, includeStates, includeProofs, includeCerts, includeDocuments, includeStampedCopies = false) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH + '/' + this.SHIPMENTS_PATH + '/' + encodeURIComponent(shipmentCode) + "?" +
      //this.SHIPMENT_CODE_PATH + "=" + shipmentCode + "&" +
      this.INCLUDE_STATES_QUERY + "=" + includeStates + "&" +
      this.INCLUDE_PROOFS_QUERY + "=" + includeProofs + "&" +
      this.INCLUDE_CERTS_QUERY + "=" + includeCerts + "&" +
      this.INCLUDE_DOCUMENTS_QUERY + "=" + includeDocuments + "&" +
      this.INCLUDE_STAMPED_COPIES_QUERY + "=" + includeStampedCopies
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the list of feedback returns available associated with a particular shipment.
   * @param {string} shipmentCode External identifiers of the shipments asking for their feedback
   * @param {boolean} includeStates Whether desired to receive state changes
   * @param {boolean} includeProofs Whether desired to receive proofs of delivery
   * @param {boolean} includeCerts Whether desired to receive certificates
   * @param {boolean} includeStampedCopies Whether desired to receive stamped copies
   * @returns {FeedbackReturn[]} (string returnId, string returnType, string description, string shipmentCode, DateTime recordedTime, bool read)
  */
  static async GetFeedbackOfShipments(shipmentCodes, includeStates, includeProofs, includeCerts, includeDocuments, includeStampedCopies = false) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH + '/' + this.SHIPMENTS_PATH + "?" +
      this.INCLUDE_STATES_QUERY + "=" + includeStates + "&" +
      this.INCLUDE_PROOFS_QUERY + "=" + includeProofs + "&" +
      this.INCLUDE_CERTS_QUERY + "=" + includeCerts + "&" +
      this.INCLUDE_DOCUMENTS_QUERY + "=" + includeDocuments + "&" +
      this.INCLUDE_STAMPED_COPIES_QUERY + "=" + includeStampedCopies
    var body = shipmentCodes
    
    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the number of feedback return units available for the calling user.
   * This function can be used to periodically poll for new feedback.
   * @param {boolean} includeRead Whether desired to receive return units flagged as read, otherwise only unread feedback will be included
   * @param {boolean} includeStates Whether desired to receive state changes
   * @param {boolean} includeProofs Whether desired to receive proofs of delivery
   * @param {boolean} includeCerts Whether desired to receive certificates
   * @param {boolean} includeReports Whether desired to receive reports availability
   * @param {boolean} includeStampedCopies Whether desired to receive stamped copies
   * @returns {int}
  */
  static async CountFeedback(includeRead, includeStates, includeProofs, includeCerts, includeDocuments, includeReports, includeStampedCopies = false) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH + '/' + this.COUNT_PATH + "?" +
      this.INCLUDE_READ_QUERY + "=" + includeRead + "&" +
      this.INCLUDE_STATES_QUERY + "=" + includeStates + "&" +
      this.INCLUDE_PROOFS_QUERY + "=" + includeProofs + "&" +
      this.INCLUDE_CERTS_QUERY + "=" + includeCerts + "&" +
      this.INCLUDE_DOCUMENTS_QUERY + "=" + includeDocuments + "&" +
      this.INCLUDE_REPORTS_QUERY + "=" + includeReports + "&" +
      this.INCLUDE_STAMPED_COPIES_QUERY + "=" + includeStampedCopies
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the number of feedback return units available for the billable administered by the calling administrator.
   * This is to be used by working group administrators which are allowed to access content from any user in the billable.
   * This function can be used to periodically poll for new feedback.
   * @param {boolean} includeRead Whether desired to receive return units flagged as read, otherwise only unread feedback will be included
   * @param {boolean} includeStates Whether desired to receive state changes
   * @param {boolean} includeProofs Whether desired to receive proofs of delivery
   * @param {boolean} includeCerts Whether desired to receive certificates
   * @param {boolean} includeReports Whether desired to receive reports availability
   * @param {boolean} includeStampedCopies Whether desired to receive stamped copies
   * @returns {int}
  */
  static async CountFeedbackOfBillable(includeRead, includeStates, includeProofs, includeCerts, includeDocuments, includeReports, includeStampedCopies = false) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_FEEDBACK_PATH + '/' + this.COUNT_PATH + "?" +
      this.INCLUDE_READ_QUERY + "=" + includeRead + "&" +
      this.INCLUDE_STATES_QUERY + "=" + includeStates + "&" +
      this.INCLUDE_PROOFS_QUERY + "=" + includeProofs + "&" +
      this.INCLUDE_CERTS_QUERY + "=" + includeCerts + "&" +
      this.INCLUDE_DOCUMENTS_QUERY + "=" + includeDocuments + "&" +
      this.INCLUDE_REPORTS_QUERY + "=" + includeReports + "&" +
      this.INCLUDE_STAMPED_COPIES_QUERY + "=" + includeStampedCopies
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Sets some feedback return units as already read.
   * @param {string[]} returnIds Whether desired to receive return units flagged as read, otherwise only unread feedback will be included
   * @returns {int} If only one feedback was requested: 1 or an error message; if several feedback were requested: the actual number of return units flagged as read
  */
  static async ReadFeedback(returnIds) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH + '/' + this.FLAG_READ_PATH
    var body = returnIds

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Sets some feedback return units as not yet read.
   * @param {string[]} returnIds Whether desired to receive return units flagged as read, otherwise only unread feedback will be included
   * @returns {int} If only one feedback was requested: 1 or an error message; if several feedback were requested: the actual number of return units flagged as read
  */
  static async UnreadFeedback(returnIds) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH + '/' + this.FLAG_UNREAD_PATH
    var body = returnIds

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
    * The calling user will be called back by the inbox service when the
    * current number of unread feedback return units increases, ie. when
    * new feedback becomes available in the service for the calling user.
    * If the calling user is an administrator of a billable the callback
    * will occur for the feedback owned by this admin user (not the whole billable).
    * 
    * Note this registration applies to the pair user/billable. So a same user
    * may register to watch feedback for different billables this user has
    * a role in them.
    * 
    * The callback will not occur immediately for every new feedback but
    * grouping incoming feedback in a single callback.
    * 
    * The callback is a POST request to the URL specified adding the following
    * query parameters: "?total=10&states=5&proofs=2&certs=3&reports=0"
    * for including the number of current unread return units per type.
    * 
    * The response of the callback must be a 200 OK (or 205) including as content a string
    * specified by the callbackResponse parameter in this registration, otherwise 
    * the remote server will be considered failing or unreliable.
    * 
    * The remote server may responde with code 205 "Reset Content". In this case all
    * unread feedback will be immediately set as read.
    * @param {Watcher} watcher Registration information
    * @returns {int} The total number of feedback return units unread at the time of this call
  */
  static async WatchFeedback(watcher) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.WATCHERS_PATH
    var body = watcher

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
    * Same as WatchFeedback() but the calling user must be administrator of the billable
    * of the calling session.
    * The administrator will receive callbacks about changes in the unread feedback from the whole billable.
    * @param {Watcher} watcher Registration information
    * @returns {int} The total number of feedback return units unread at the time of this call
  */
  static async WatchFeedbackOfBillable(watcher) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_WATCHERS_PATH
    var body = watcher

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
    * The calling user will stop being called back if was previously registered.
    * Note this deregistration refers to the pair user/billable. So, if this user is
    * registered for other billable (not the one associated with the session) this other
    * registration will remain active. 
    * @returns {bool} Whether the calling user was actually watching
  */
  static async UnwatchFeedback() {
    var verb = 'DELETE'
    var path = this.SERVICE_ROUTE + '/' + this.WATCHERS_PATH
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
    * Provides the content of a feedback file (a proof, a certificate or a report).
    * @param {returnId} Internal identifier of the feedback return asking for its associated file
    * @returns {byte[]} The content of the file for browser download
  */
  static async DownloadFeedbackFile(returnId) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_FILE_PATH + "/" + returnId
    var body = null

    var fileName = null
    return fetch(this.devPrefix + path, AuthCli.GetRequestOptions(verb, body, true))
      .then(response => {

        if (response.status === 200) {
          const header = response.headers.get('Content-Disposition');
          const parts = header ? header.split(';') : null;
          fileName = parts ? parts[1].split('=')[1] : null;
          return response.blob()
        }
        // Return API errors, including validation failures such as a download batch that is too large.
        else return response.text()
      })
      .then(blobby => {
        if (blobby == null) return
        if (blobby.toString().startsWith('[ERR')) return blobby.toString()

        // Force the received content to appear as a downloaded file.
        let anchor = document.createElement("a");
        document.body.appendChild(anchor);
        let objectUrl = window.URL.createObjectURL(blobby);
        anchor.href = objectUrl;
        anchor.download = fileName ? fileName : "retorno_" + returnId + ".pdf";
        anchor.click();
        window.URL.revokeObjectURL(objectUrl);
      });
  }

  /**
   * Generates and provides a ZIP file containing feedback documents.
   * @param {string[]} shipmentCodes External unique identifiers of shipments asking for their available feedback
   * @param {bool} includeRead >Whether desired to include return units flagged as read, otherwise only unread feedback will be included
   * @param {bool} flagAsRead Whether desired that the service will automatically set all provided return units as read
   * @param {bool} includeProofs Whether desired to include proofs of delivery documents
   * @param {bool} includeCerts Whether desired to receive certificate documents
   * @param {bool} includeDocuments Whether desired to receive shipment's documents
   * @param {boolean} includeStampedCopies Whether desired to receive stamped copies
   * @returns {byte[]} The content of the file for browser download
 */
  static async DownloadFeedbackOfShipments(shipmentCodes, includeRead, flagAsRead, includeProofs, includeCerts, includeDocuments, includeStampedCopies = false) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_FILE_PATH + "?" +
      this.INCLUDE_READ_QUERY + "=" + includeRead + "&" +
      this.FLAG_AS_READ_QUERY + "=" + flagAsRead + "&" +
      this.INCLUDE_PROOFS_QUERY + "=" + includeProofs + "&" +
      this.INCLUDE_CERTS_QUERY + "=" + includeCerts + "&" +
      this.INCLUDE_DOCUMENTS_QUERY + "=" + includeDocuments + "&" +
      this.INCLUDE_STAMPED_COPIES_QUERY + "=" + includeStampedCopies
    var body = shipmentCodes

    var fileName = null
    return fetch(this.devPrefix + path, AuthCli.GetRequestOptions(verb, body, false))
      .then(response => {

        if (response.status === 200) {
          const header = response.headers.get('Content-Disposition');
          const parts = header ? header.split(';') : null;
          fileName = parts ? parts[1].split('=')[1] : null;
          return response.blob()
        }
        // Return API errors, including validation failures such as a download batch that is too large.
        else return response.text()
      })
      .then(blobby => {
        if (blobby == null) return
        if (blobby.toString().startsWith('[ERR')) return blobby.toString()

        // Force the received content to appear as a downloaded file.
        let anchor = document.createElement("a");
        document.body.appendChild(anchor);
        let objectUrl = window.URL.createObjectURL(blobby);
        anchor.href = objectUrl;
        anchor.download = fileName ? fileName : "retornos.pdf";
        anchor.click();
        window.URL.revokeObjectURL(objectUrl);
      });
  }

  /**
  * Generates and provides a ZIP file containing feedback documents.
  * This method is for billable administrators.
  * @param {string[]} shipmentCodes External unique identifiers of shipments asking for their available feedback
  * @param {bool} includeRead >Whether desired to include return units flagged as read, otherwise only unread feedback will be included
  * @param {bool} flagAsRead Whether desired that the service will automatically set all provided return units as read
  * @param {bool} includeProofs Whether desired to include proofs of delivery documents
  * @param {bool} includeCerts Whether desired to receive certificate documents
  * @param {bool} includeDocuments Whether desired to receive shipment's documents
   * @param {boolean} includeStampedCopies Whether desired to receive stamped copies
  * @returns {byte[]} The content of the file for browser download
 */
  static async DownloadFeedbackOfShipmentsOfBillable(shipmentCodes, includeRead, flagAsRead, includeProofs, includeCerts, includeDocuments, includeStampedCopies = false) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_FEEDBACK_FILE_PATH + "?" +
      this.INCLUDE_READ_QUERY + "=" + includeRead + "&" +
      this.FLAG_AS_READ_QUERY + "=" + flagAsRead + "&" +
      this.INCLUDE_PROOFS_QUERY + "=" + includeProofs + "&" +
      this.INCLUDE_CERTS_QUERY + "=" + includeCerts + "&" +
      this.INCLUDE_DOCUMENTS_QUERY + "=" + includeDocuments + "&" +
      this.INCLUDE_STAMPED_COPIES_QUERY + "=" + includeStampedCopies
    var body = shipmentCodes

    var fileName = null
    return fetch(this.devPrefix + path, AuthCli.GetRequestOptions(verb, body, false))
      .then(response => {

        if (response.status === 200) {
          const header = response.headers.get('Content-Disposition');
          const parts = header ? header.split(';') : null;
          fileName = parts ? parts[1].split('=')[1] : null;
          return response.blob()
        }
        else if (response.status === 500) return response.text()
      })
      .then(blobby => {
        if (blobby == null) return
        if (blobby.toString().startsWith('[ERR')) return blobby.toString()

        // Force the received content to appear as a downloaded file.
        let anchor = document.createElement("a");
        document.body.appendChild(anchor);
        let objectUrl = window.URL.createObjectURL(blobby);
        anchor.href = objectUrl;
        anchor.download = fileName ? fileName : "retornos.pdf";
        anchor.click();
        window.URL.revokeObjectURL(objectUrl);
      });
  }
  /**
      * Generates and provides a XLS file containing shipments info.
      * The calling user must be the owner of the shipment 
      * @param {string[]} returnIds External unique identifiers of feedbacks asking for their available info
      * @returns {byte[]} The content of the file for browser download
    */
  static async DownloadFeedbacksXLS(returnIds, fileName) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH
    var body = returnIds

    return fetch(this.devPrefix + path, AuthCli.GetRequestOptions(verb, body, false))
      .then(response => {

        if (response.status === 200) return response.blob()
        else if (response.status === 500) return response.text()

      })
      .then(blobby => {
        if (blobby == null) return
        if (blobby.toString().startsWith('[ERR')) return blobby.toString()

        // Force the received content to appear as a downloaded file.
        let anchor = document.createElement("a");
        document.body.appendChild(anchor);
        let objectUrl = window.URL.createObjectURL(blobby);
        anchor.href = objectUrl;
        anchor.download = fileName;
        anchor.click();
        window.URL.revokeObjectURL(objectUrl);
      });
  }
   
  /**
    * Generates and provides a XLS file containing shipments info.
    * The calling user must be billable admin of the shipment
    * @param {string[]} returnIds External unique identifiers of feedbacks asking for their available info
    * @returns {byte[]} The content of the file for browser download
  */
  static async DownloadFeedbacksOfBillableXLS(returnIds, fileName) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.FEEDBACK_PATH
    var body = returnIds

    return fetch(this.devPrefix + path, AuthCli.GetRequestOptions(verb, body, false))
      .then(response => {

        if (response.status === 200) return response.blob()
        else if (response.status === 500) return response.text()

      })
      .then(blobby => {
        if (blobby == null) return
        if (blobby.toString().startsWith('[ERR')) return blobby.toString()

        // Force the received content to appear as a downloaded file.
        let anchor = document.createElement("a");
        document.body.appendChild(anchor);
        let objectUrl = window.URL.createObjectURL(blobby);
        anchor.href = objectUrl;
        anchor.download = fileName;
        anchor.click();
        window.URL.revokeObjectURL(objectUrl);
      });
  }
}


// WEBPACK FOOTER //
// ./src/sdk/InboxCli.js